import type { IdCardSides } from './IdCardCapture';
import { emptyIdCardSides } from './IdCardCapture';

export function isMinorFromBirthDate(birthDate?: string): boolean {
  if (!birthDate) return false;
  return (Date.now() - new Date(birthDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25) < 18;
}

export type IdCardPhotoPayload = {
  role: string;
  side: 'front' | 'back';
  dataUrl: string;
  capturedAt: string;
};

/**
 * Parse contract.idCardPhotos into recto/verso per role.
 * Legacy entries without `side` are treated as front (recto).
 */
export function parseIdCardPhotosFromRaw(raw: unknown): {
  client: IdCardSides;
  guardian: IdCardSides;
} {
  const client = emptyIdCardSides();
  const guardian = emptyIdCardSides();
  let photos: unknown = raw;
  if (typeof photos === 'string') {
    try {
      photos = JSON.parse(photos);
    } catch {
      return { client, guardian };
    }
  }
  if (!Array.isArray(photos)) return { client, guardian };

  for (const row of photos) {
    if (!row || typeof (row as any).dataUrl !== 'string') continue;
    const role = String((row as any).role ?? '').toUpperCase();
    const rawSide = String((row as any).side ?? 'front').toLowerCase();
    const side: 'front' | 'back' = rawSide === 'back' ? 'back' : 'front';
    const target = role === 'GUARDIAN' ? guardian : role === 'CLIENT' ? client : null;
    if (!target) continue;
    // Keep first photo per side (or overwrite with later — prefer last non-empty)
    target[side] = (row as any).dataUrl;
  }
  return { client, guardian };
}

export function buildIdCardPhotosPayload(
  client: IdCardSides,
  guardian: IdCardSides,
  capturedAt = new Date().toISOString(),
): IdCardPhotoPayload[] {
  const photos: IdCardPhotoPayload[] = [];
  if (client.front) photos.push({ role: 'CLIENT', side: 'front', dataUrl: client.front, capturedAt });
  if (client.back) photos.push({ role: 'CLIENT', side: 'back', dataUrl: client.back, capturedAt });
  if (guardian.front) photos.push({ role: 'GUARDIAN', side: 'front', dataUrl: guardian.front, capturedAt });
  if (guardian.back) photos.push({ role: 'GUARDIAN', side: 'back', dataUrl: guardian.back, capturedAt });
  return photos;
}
